import type { AnimationCategory, AnimationPreset } from "./types";

/** id → preset. A plain object so `animationPresets[id]` and
 * Object.values() call sites work directly; insertion order defines the
 * display order within a category. */
export const animationPresets: Record<string, AnimationPreset> = {};

/**
 * Registers (or overrides) a preset. Use this to add custom presets — the
 * panel UI derives everything (tile preview, settings controls, param
 * inputs) from the preset's `category`, `defaults` and `paramsSchema`, so a
 * registered preset is immediately usable and exportable with no other
 * changes.
 */
export const registerAnimationPreset = (
  preset: AnimationPreset,
): AnimationPreset => {
  animationPresets[preset.id] = preset;
  return preset;
};

export const registerAnimationPresets = (
  presets: readonly AnimationPreset[],
) => {
  for (const preset of presets) {
    registerAnimationPreset(preset);
  }
};

export const presetsByCategory = (
  category: AnimationCategory,
): AnimationPreset[] =>
  Object.values(animationPresets).filter(
    (preset) => preset.category === category,
  );

/** Presets that accept a custom drawn shape (`customPath`) as the morph
 * target: everything with a "shape" param — morph AND template presets that
 * route through morphStatics — plus morphToElement (its no-target fallback). */
export const supportsCustomPath = (presetId: string) =>
  presetId === "morphToElement" ||
  (animationPresets[presetId]?.paramsSchema.some((p) => p.key === "shape") ??
    false);

/** Presets whose transform origin is user-placeable (`pivotX`/`pivotY`
 * params, normalized bbox coords) — they get the "Pick anchor on canvas"
 * button (animation/anchorPick.ts). */
export const supportsAnchorPick = (presetId: string) =>
  animationPresets[presetId]?.paramsSchema.some((p) => p.key === "pivotX") ??
  false;
